import { createContext, useContext } from 'react';
import type { SyncManager, SyncSnapshot } from './sync.ts';

/**
 * Light half of the offline sync context (no IndexedDB code): the context object and the hooks that read it. The
 * provider with the sync engine and Dexie (sync-context.tsx, about 25 KB compressed) is loaded as a separate chunk by
 * SyncHost (sync-host.tsx), so it is not on the first screen's critical path.
 */

/** What the screens call on the engine. All asynchronous, so a call made while the engine loads simply waits. */
export type SyncActions = Pick<
  SyncManager,
  'saveOffline' | 'flush' | 'discardLocal' | 'reapplyOnServerRevision'
>;

export interface SyncCtx {
  manager: SyncActions;
  snapshot: SyncSnapshot;
  persistent: boolean | null;
}

export const SyncContext = createContext<SyncCtx | null>(null);

/**
 * Set by SyncHost: `placeholder` is the context value while the engine loads, `demand()` starts loading it now (a
 * screen uses it). Null when SyncProvider is mounted directly.
 */
export const SyncReady = createContext<{ placeholder: SyncCtx; demand: () => void } | null>(null);

export const IDLE_SNAPSHOT: SyncSnapshot = {
  state: 'ONLINE',
  pending: 0,
  conflicts: 0,
  rejected: 0,
  lastServerAck: null,
  lastError: null,
  expiring: 0,
  nextExpiryAt: null,
};

/**
 * Engine and state. Never suspends: while the engine loads, the snapshot is the idle one ("Conectado", nothing
 * pending) and engine calls wait for it. A screen that reads it asks the host to load the engine at once. (A
 * suspending version made the movement screen wait for React's 300 ms reveal throttle on every deep link.)
 */
export function useSync(): SyncCtx {
  const value = useContext(SyncContext);
  const ready = useContext(SyncReady);
  if (!value) throw new Error('SyncProvider ausente');
  if (ready && value === ready.placeholder) ready.demand();
  return value;
}

/** State only, without asking for the engine (header pill): "Conectado" until the engine reports otherwise. */
export function useSyncSnapshot(): SyncSnapshot {
  return useContext(SyncContext)?.snapshot ?? IDLE_SNAPSHOT;
}
